import {Icon, TextInput, Tooltip} from '@/components/atoms'
import {defaultTo} from 'lodash'
import {useCallback, useEffect, useState, useRef} from 'react'
import './text-input.scss'
import useTextInputValidation from './useTextInputValidation-hook'
/**
 * TextInputField component renders a text input field with various configurations.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.value - The current value of the input field.
 * @param {function} props.updateValue - Function to update the value of the input field.
 * @param {string} props.label - The label for the input field.
 * @param {Object} props.schema - The schema defining the input field type and validation.
 * @param {Object} props.uiSchema - The UI schema defining additional UI properties.
 * @param {boolean} [props.required=false] - Whether the input field is required.
 * @param {boolean} [props.disabled=false] - Whether the input field is disabled.
 * @param {boolean} props.visible - Whether the input field is visible.
 * @param {string} [props.suffix] - Optional suffix to display after the input value (e.g., "x").
 *
 * @returns {JSX.Element|null} The rendered TextInputField component or null if not visible.
 */

// Constants for suffix positioning
const SUFFIX_CONSTANTS = {
  INPUT_PADDING_LEFT: 12, // Must match input field's left padding
  SUFFIX_SPACING: 8, // Space between text and suffix
  SUFFIX_FINE_TUNE: 2, // Fine adjustment for positioning
  SUFFIX_BOTTOM_POSITION: 13, // Bottom position from input bottom
  HIDDEN_POSITION: -1000 // Position to hide suffix off-screen
}

const TextInputField = ({value, updateValue, label, schema, uiSchema, required = false, disabled = false, visible, suffix}) => {
  const {onChange} = useTextInputValidation(label, uiSchema?.validationRules)
  const [error, setError] = useState()
  const [lastValidValue, setLastValidValue] = useState(value)
  const [suffixLeft, setSuffixLeft] = useState(0)
  const hiddenSpanRef = useRef(null)

  const updateSuffixPosition = useCallback((inputValue) => {
    const inputStr = String(inputValue)

    if (!suffix || !hiddenSpanRef.current || inputValue === null || inputValue === undefined || inputStr === '') {
      setSuffixLeft(SUFFIX_CONSTANTS.HIDDEN_POSITION)
      return
    }

    hiddenSpanRef.current.textContent = inputStr

    void hiddenSpanRef.current.offsetHeight
    const textWidth = hiddenSpanRef.current.offsetWidth

    const calculatedLeft = Math.max(textWidth, 1) + SUFFIX_CONSTANTS.SUFFIX_SPACING
    setSuffixLeft(calculatedLeft)
  }, [suffix])

  const clearValidationError = val => {
    const validationError = onChange(val)
    if (!validationError) {
      setError(undefined)
      setLastValidValue(val)
      updateValue(val, false)
    }
  }

  const handleValidationError = useCallback(
    (errorMessage, val) => {
      setError(errorMessage)
      updateValue(val, true)
    },
    [updateValue]
  )

  const handleOnChange = e => {
    let newValue = typeof e === 'string' ? e : e?.target?.value

    if (schema?.type === 'number' && newValue) {
      newValue = String(newValue).replace(/,/g, '')

      const decimalMatch = newValue.match(/^\d*\.?\d{0,2}/)
      if (decimalMatch) {
        newValue = decimalMatch[0]
      }
    }

    const validationError = onChange(newValue)

    updateSuffixPosition(newValue)

    if (validationError) {
      handleValidationError(validationError, newValue)
    } else if (error || newValue !== lastValidValue) {
      clearValidationError(newValue)
    }
  }

  useEffect(() => {
    // validate default value and update suffix position
    if (value || value === '') {
      handleOnChange(value)
    }
    updateSuffixPosition(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, updateSuffixPosition])

  const getInputType = schema => {
    return schema?.type === 'integer' ? 'number' : schema?.type || 'text'
  }

  const shouldRenderSuffix = (value) => {
    return value !== null && value !== undefined && String(value) !== '' && suffixLeft > 0
  }

  const handleKeyDown = e => {
    const {key} = e
    const inputElement = e.target
    const currentValue = inputElement ? inputElement.value : String(value || '')

    if (schema?.restrictDecimal) {
      if (['.', ',', 'e', '-'].includes(key) || (!currentValue && key === '0')) {
        e.preventDefault()
      }
    } else if (schema?.type === 'number') {
      if (['e', 'E', '+', '-', ','].includes(key)) {
        e.preventDefault()
        return
      }

      if (['ArrowUp', 'ArrowDown'].includes(key)) {
        e.preventDefault()
        return
      }

      if (/^\d$/.test(key)) {
        const decimalIndex = currentValue.indexOf('.')
        if (decimalIndex !== -1) {
          const decimalPlaces = currentValue.substring(decimalIndex + 1).length
          if (decimalPlaces >= 2) {
            e.preventDefault()
          }
        }
      }
    }
  }

  const disableButton = disabled || uiSchema?.readOnly ? 'disable-button' : ''

  return visible ? (
    <>
      <div className={`input-text-wrapper ${suffix ? 'input-with-suffix' : ''} ${uiSchema?.className || ''}`}>
        <div className="input-with-suffix-container">
          <TextInput
            required={required}
            className={`${uiSchema?.className ?? ''} ${disableButton} inputType-${getInputType(schema)}`}
            disabled={disabled || uiSchema?.readOnly || uiSchema?.disabled}
            maxLength={uiSchema?.maxLength ?? undefined}
            type={getInputType(schema)}
            error={error}
            label={label}
            onChange={handleOnChange}
            isDark={uiSchema?.isDark || false}
            value={defaultTo(!value ? '' : value, undefined)}
            placeholderText={uiSchema?.placeholder ?? ''}
            helperText={uiSchema?.helperText}
            handleKeyDown={handleKeyDown}
          />
          {suffix && (
            <>
              {/* Hidden span to measure text width */}
              <span
                ref={hiddenSpanRef}
                style={{
                  position: 'absolute',
                  visibility: 'hidden',
                  whiteSpace: 'pre',
                  fontSize: '1rem',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontWeight: 'normal',
                  letterSpacing: 'normal',
                  lineHeight: 'normal',
                  textAlign: 'left',
                  left: `${SUFFIX_CONSTANTS.INPUT_PADDING_LEFT}px`,
                  top: 0,
                  zIndex: -1,
                  border: 'none',
                  padding: 0,
                  margin: 0
                }}
              />
              {/* Suffix positioned after text - only show when there's text */}
              {shouldRenderSuffix(value) && (
                <span
                  className="input-suffix-dynamic"
                  style={{
                    position: 'absolute',
                    left: `${suffixLeft + SUFFIX_CONSTANTS.SUFFIX_FINE_TUNE}px`,
                    bottom: `${SUFFIX_CONSTANTS.SUFFIX_BOTTOM_POSITION}px`,
                    color: 'var(--color_FFFFFF)',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    fontSize: '1rem',
                    zIndex: 10
                  }}
                >
                  {suffix}
                </span>
              )}
            </>
          )}
        </div>
        {uiSchema?.tooltipText && uiSchema?.tooltipText?.length > 0 && (
          <div className="tooltip-text">
            <Tooltip text={uiSchema.tooltipText}>
              <Icon alt="" icon="info" size="small" />
            </Tooltip>
          </div>
        )}
      </div>

      {!error && uiSchema.helpText && (
        <div className="input-help-text items-center">
          <Tooltip text={uiSchema.helpText} className="help-tooltip">
            <Icon icon="info" size="small" />
          </Tooltip>
          {uiSchema.helpText ? uiSchema.helpText : 'Help'}
        </div>
      )}
    </>
  ) : null
}

export default TextInputField
